// Memory utility for storing user preferences and settings
class Memory {
  constructor() {
    this.storageKey = 'userMemory';
    this.defaultSettings = {
      theme: 'dark',
      language: 'en',
      notifications: true,
      autoPlay: false,
      volume: 0.7,
      lastVisit: null,
      visitCount: 0,
      preferences: {
        showAgeVerification: true,
        rememberLogin: false,
        analytics: true,
        cookies: true,
      },
    };
  }

  // Initialize memory with default settings
  init() {
    const existing = this.get();
    if (!existing) {
      this.set(this.defaultSettings);
    }
    this.updateVisitInfo();
  }

  // Get all memory data
  get() {
    try {
      const data = localStorage.getItem(this.storageKey);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error reading memory:', error);
      return null;
    }
  }

  // Set memory data
  set(data) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving memory:', error);
      return false;
    }
  }

  // Update specific setting
  updateSetting(key, value) {
    const memory = this.get() || this.defaultSettings;
    const keys = key.split('.');
    let current = memory;

    for (let i = 0; i < Math.max(0, keys.length - 1); i++) {
      if (!current[keys[i]]) {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }

    const lastKey = keys[Math.max(0, keys.length - 1)];
    if (lastKey) {
      current[lastKey] = value;
    }
    return this.set(memory);
  }

  // Get specific setting
  getSetting(key) {
    const memory = this.get();
    if (!memory) return null;

    const keys = key.split('.');
    let current = memory;

    for (const k of keys) {
      if (current && typeof current === 'object' && k in current) {
        current = current[k];
      } else {
        return null;
      }
    }

    return current;
  }

  // Update visit information
  updateVisitInfo() {
    const memory = this.get() || this.defaultSettings;
    memory.lastVisit = new Date().toISOString();
    memory.visitCount = (memory.visitCount || 0) + 1;
    this.set(memory);
  }

  // Clear all memory
  clear() {
    try {
      localStorage.removeItem(this.storageKey);
      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error clearing memory:', error);
      return false;
    }
  }

  // Export memory data
  export() {
    return this.get();
  }

  // Import memory data
  import(data) {
    if (typeof data === 'object' && data !== null) {
      return this.set(data);
    }
    return false;
  }

  // Get user preferences
  getPreferences() {
    return this.getSetting('preferences') || this.defaultSettings.preferences;
  }

  // Update user preferences
  updatePreferences(preferences) {
    return this.updateSetting('preferences', {
      ...this.getPreferences(),
      ...preferences,
    });
  }

  // Check if user has visited before
  isReturningUser() {
    const visitCount = this.getSetting('visitCount') || 0;
    return visitCount > 1;
  }

  // Get last visit date
  getLastVisit() {
    const lastVisit = this.getSetting('lastVisit');
    return lastVisit ? new Date(lastVisit) : null;
  }

  // Get visit count
  getVisitCount() {
    return this.getSetting('visitCount') || 0;
  }
}

// Create singleton instance
const memory = new Memory();

// Initialize memory on first load
memory.init();

export default memory;
